import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { Item } from '../../../styles/common';
import { contactPath, crmReturnState } from '../crmPaths';
import { MERGE_NB_MATCH } from './matchReview.queries';
import {
  Agreement,
  agreementOf,
  candidateBlock,
  candidateName,
  describeDecision,
  describeMerge,
  describeReason,
  describeReviewError,
  evictAfterDecision,
  describeSignupPhone,
  MatchCandidate,
  MatchItem,
  PHONE_TYPES,
  ReviewState,
  RULES,
  ruleLabel,
  signupName,
} from './matchReview';

const NONE = (
  <Box component="span" sx={{ color: '#cbd5e1' }}>
    —
  </Box>
);

/** A value, with a tick when it agrees with the NationBuilder record. */
const Agreed = ({ agrees, children }: { agrees?: boolean; children: React.ReactNode }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'flex-start', gap: 0.5 }}>
    {agrees && <CheckCircleIcon titleAccess="Agrees with the NationBuilder record" sx={{ fontSize: 15, color: 'success.main', mt: '2px' }} />}
    <span>{children}</span>
  </Box>
);

const day = (value: string | null) => (value ? dayjs(value).format('D MMM YYYY') : null);

/** "Atlanta, GA, 30301", each part once: a region that repeats the town is left out. */
const place = (...parts: Array<string | null>) =>
  parts
    .filter((part, index): part is string => Boolean(part) && parts.findIndex((p) => p?.trim().toLowerCase() === part?.trim().toLowerCase()) === index)
    .join(', ') || null;

interface MatchReviewCardProps {
  item: MatchItem;
  stale: boolean;
  onReview: (item: MatchItem, review: ReviewState) => void;
}

/**
 * One NationBuilder record beside the app account (or accounts) it may be.
 * Values that agree carry a tick: names, emails, street, town, country, and
 * the app's id on the record. Phones are shown as stored and never ticked:
 * whether two numbers are the same is the match ladder's call (card 06).
 */
export const MatchReviewCard = ({ item, stale, onReview }: MatchReviewCardProps) => {
  const here = useLocation();
  const signup = item.signup;
  const name = signupName(signup, item.nbSignupId);
  const agreements = item.candidates.map((c) => agreementOf(signup, c));
  const decided = item.review === 'APPROVED' || item.review === 'REJECTED';
  const linked = item.review === 'APPROVED' ? item.userId : null;
  // Card 28: once merged, the record's own contact is the merged-away one; `contactId` is the member's.
  const recordContactId = item.merge?.fromContactId ?? item.contactId;
  const [mergeError, setMergeError] = useState<string | null>(null);
  const [merge, { loading: merging }] = useMutation(MERGE_NB_MATCH, { update: (cache) => evictAfterDecision(cache) });
  const joinContacts = async () => {
    setMergeError(null);
    try {
      await merge({ variables: { nbSignupId: item.nbSignupId } });
    } catch (error) {
      setMergeError(describeReviewError(error as Error));
    }
  };

  const row = (label: string, nb: React.ReactNode, account: (c: MatchCandidate, agrees: Agreement) => React.ReactNode) => (
    <TableRow>
      <TableCell component="th" scope="row" sx={{ color: 'text.secondary', fontWeight: 500, width: 110 }}>
        {label}
      </TableCell>
      <TableCell>{nb ?? NONE}</TableCell>
      {item.candidates.map((c, index) => (
        <TableCell key={c.userId} sx={{ opacity: candidateBlock(c) && !decided ? 0.6 : 1 }}>
          {account(c, agreements[index]) ?? NONE}
        </TableCell>
      ))}
    </TableRow>
  );

  return (
    <Item role="article" aria-label={`NationBuilder record ${name}`} data-signup={item.nbSignupId} sx={{ p: 0, overflow: 'hidden', opacity: stale ? 0.6 : 1 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, px: { xs: 1.5, sm: 2 }, pt: 1.5, pb: 1 }}>
        <Typography variant="h6" component="h2" sx={{ fontSize: '1rem', mr: 0.5 }}>
          {name}
        </Typography>
        <Tooltip describeChild title={RULES[item.rule]?.hint ?? ''}>
          <Chip size="small" label={ruleLabel(item.rule)} color={item.rule === 6 ? 'warning' : 'default'} variant="outlined" sx={{ fontWeight: 600 }} />
        </Tooltip>
        {!decided && (
          <Typography variant="body2" color="text.secondary">
            {describeReason(item)}
          </Typography>
        )}
        {item.corroboratedBy.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            · also agrees: {item.corroboratedBy.join(', ')}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          NationBuilder #{item.nbSignupId}
        </Typography>
      </Box>

      {item.sameAccountAs.length > 0 && !decided && (
        <Alert severity="warning" sx={{ mx: { xs: 1.5, sm: 2 }, mb: 1, py: 0 }}>
          {item.sameAccountAs.length === 1 ? 'Another NationBuilder record' : `${item.sameAccountAs.length} other NationBuilder records`} look
          {item.sameAccountAs.length === 1 ? 's' : ''} like this account too (#{item.sameAccountAs.join(', #')}). Approve one at most: with
          several, the name and town point at a namesake as readily as at the member.
        </Alert>
      )}

      {decided && (
        <Box sx={{ mx: { xs: 1.5, sm: 2 }, mb: 1, px: 1.5, py: 1, borderRadius: 1, bgcolor: item.review === 'APPROVED' ? 'rgba(22,163,74,0.08)' : '#f1f5f9' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {describeDecision(item)}
          </Typography>
          {item.reviewNote && (
            <Typography variant="body2" sx={{ mt: 0.25, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              “{item.reviewNote}”
            </Typography>
          )}
          {item.merge && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {describeMerge(item.merge)}{' '}
              <Link component={RouterLink} to={contactPath(item.merge.intoContactId)} state={crmReturnState(here)}>
                Open the contact
              </Link>
            </Typography>
          )}
        </Box>
      )}

      {item.mergeable && (
        <Alert
          severity="warning"
          sx={{ mx: { xs: 1.5, sm: 2 }, mb: 1, py: 0, alignItems: 'center' }}
          action={
            <Button
              size="small"
              color="inherit"
              onClick={joinContacts}
              disabled={merging}
              startIcon={merging ? <CircularProgress size={12} color="inherit" /> : undefined}
              aria-label={`Join the two contacts of ${name}`}
            >
              Join the two contacts
            </Button>
          }
        >
          Approved before merging was built: the NationBuilder record and the member are still two contacts.
          {mergeError && (
            <Box component="span" sx={{ display: 'block', color: 'error.main', fontWeight: 500 }}>
              {mergeError}
            </Box>
          )}
        </Alert>
      )}

      <TableContainer
        sx={{
          '& .MuiTableCell-root': { borderColor: '#eef1f5', px: 1.25, py: 0.75, fontSize: '0.8125rem', verticalAlign: 'top' },
          '& .MuiTableCell-head': { fontWeight: 600, bgcolor: '#f8fafc' },
        }}
      >
        <Table size="small" aria-label={`${name} beside the account${item.candidates.length === 1 ? '' : 's'}`} sx={{ minWidth: 300 + item.candidates.length * 220 }}>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>
                NationBuilder record
                {recordContactId != null && (
                  <Link component={RouterLink} to={contactPath(recordContactId)} state={crmReturnState(here)} sx={{ display: 'block', fontWeight: 400 }}>
                    {item.merge ? 'Open the merged record' : 'Open their contact'}
                  </Link>
                )}
              </TableCell>
              {item.candidates.map((c) => {
                const block = candidateBlock(c);
                return (
                  <TableCell key={c.userId}>
                    {item.candidates.length > 1 ? `Account #${c.userId}` : 'App account'}
                    {linked === c.userId && <Chip size="small" color="success" label="Approved" sx={{ ml: 1, height: 18, fontSize: '0.6875rem' }} />}
                    {block && !decided && (
                      <Typography variant="caption" color="error.main" component="div" sx={{ fontWeight: 500 }}>
                        {block}
                      </Typography>
                    )}
                    {c.contactId != null && (
                      <Link component={RouterLink} to={contactPath(c.contactId)} state={crmReturnState(here)} sx={{ display: 'block', fontWeight: 400 }}>
                        Open their contact
                      </Link>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {row('Name', signup ? name : 'Not in the archive', (c, a) => <Agreed agrees={a.name}>{candidateName(c)}</Agreed>)}
            {row(
              'Email',
              signup?.emails.length ? signup.emails.map((e) => <div key={e}>{e}</div>) : null,
              (c, a) => c.email && <Agreed agrees={a.email}>{c.email}</Agreed>,
            )}
            {row(
              'Phone',
              signup?.phones.length
                ? signup.phones.map((p) => (
                    <div key={p.type}>
                      <Box component="span" sx={{ color: 'text.secondary' }}>
                        {PHONE_TYPES[p.type] ?? p.type}:
                      </Box>{' '}
                      {describeSignupPhone(p)}
                    </div>
                  ))
                : null,
              (c) => c.phone,
            )}
            {row('Street', signup?.address1 ?? null, (c, a) => c.streetAddress && <Agreed agrees={a.street}>{c.streetAddress}</Agreed>)}
            {row(
              'Town',
              place(signup?.city ?? null, signup?.state ?? null, signup?.zip ?? null),
              (c, a) => place(c.city, c.state) && <Agreed agrees={a.town}>{place(c.city, c.state)}</Agreed>,
            )}
            {row('Country', signup?.countryCode ?? null, (c, a) => c.countryCode && <Agreed agrees={a.country}>{c.countryCode}</Agreed>)}
            {(signup?.externalId || item.rule === 1) &&
              row('App id', signup?.externalId ?? null, (c, a) => <Agreed agrees={a.appId}>{`MobileApp-${c.userId}`}</Agreed>)}
            {row(
              'Created',
              signup ? (
                <>
                  {day(signup.createdAt)}
                  {signup.isPossibleDuplicate && (
                    <Typography variant="caption" color="text.secondary" component="div">
                      NationBuilder flagged it as a possible duplicate
                    </Typography>
                  )}
                </>
              ) : null,
              (c) => day(c.createdAt),
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {item.candidateCount > item.candidates.length && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ px: { xs: 1.5, sm: 2 }, pt: 1 }}>
          {item.candidateCount.toLocaleString('en-US')} accounts fit; the first {item.candidates.length} are shown.
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 1, px: { xs: 1.5, sm: 2 }, py: 1.25 }}>
        {decided ? (
          <Button size="small" onClick={() => onReview(item, 'PENDING')} aria-label={`Reopen ${name}`}>
            Reopen…
          </Button>
        ) : (
          <>
            <Button size="small" color="inherit" variant="outlined" onClick={() => onReview(item, 'REJECTED')} aria-label={`Reject ${name}`}>
              Not the same person…
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={!item.candidates.some((c) => !candidateBlock(c))}
              onClick={() => onReview(item, 'APPROVED')}
              aria-label={`Approve ${name}`}
              sx={{ boxShadow: 'none' }}
            >
              Same person…
            </Button>
          </>
        )}
      </Box>
    </Item>
  );
};
